/**
 * PublicMapShell
 *
 * Map shell for the public share/embed pages, mirroring the authenticated
 * `MapInstance` composition on top of the hydrated store: the same SDF
 * marker/cluster layers (`UnifiedAnchorSymbolLayer`), task line/shape
 * overlays (`TaskGeometryOverlays`), note pin clicks (`NoteSymbolLayer`),
 * hover previews, 2D/3D terrain toggle (`useTerrainMode`), Poland road
 * layers (per the share's `visibleLayers`), and the full
 * `MapControlsToolbar` + read-only `LayersPanel`. Editing affordances
 * (drawing, drag, context menus, edit routes) stay disabled via the
 * public-share store mode.
 *
 * Artifact clicks route through the public-aware `useArtifactNavigation`
 * to `/share/:token/:singular/:itemId` — the page renders the detail panel.
 *
 * @module components/share/public/PublicMapShell
 */
import { memo } from 'react';
import { Map } from '@vis.gl/react-maplibre';
import { useTranslation } from 'react-i18next';

import { AddressSearchBar } from '@/components/map/AddressSearchBar';
import { MapControlsToolbar } from '@/components/map/MapControlsToolbar';
import { MarkerHoverPreview } from '@/components/map/MarkerHoverPreview';
import { NoteSymbolLayer } from '@/components/map/NoteSymbolLayer';
import { RoadLayersMapLibre } from '@/components/map/RoadLayersMapLibre';
import {
  TaskGeometryOverlays,
  UnifiedAnchorSymbolLayer,
} from '@/components/map/UnifiedAnchorSymbolLayer';
import { UserLocationLayer } from '@/components/map/UserLocationLayer';
import { MAP_CONFIG } from '@/config/maplibre';
import type { BasemapId } from '@/config/maplibre';
import { useDeviceLayout, useHideMobileChrome } from '@/hooks';
import { useLocalLocationTracking } from '@/hooks/useLocalLocationTracking';
import type { PublicShareConfig } from '@/types/share';

import { LayersPanel } from '@/components/layers/LayersPanel';

import { PublicBrandBadge } from './PublicBrandBadge';
import { PublicCustomLayerRenderer } from '../PublicCustomLayerRenderer';
import { usePublicMapShellState } from './hooks/usePublicMapShellState';
import styles from './PublicMapShell.module.css';

interface PublicMapShellProps {
  token: string;
  config: PublicShareConfig;
  /** Initial basemap from URL params (embed) — overrides the config default. */
  initialBasemapId?: BasemapId;
  /** Renders the floating AddressSearchBar (share page only). */
  withAddressSearch?: boolean;
  /**
   * On mobile, centers the brand badge at the top (between the list-toggle and
   * the language/theme controls). Share page only — embed keeps it bottom-right.
   */
  centerBrandOnMobile?: boolean;
  /**
   * On desktop, shifts the address search right so the floating list-expand
   * toggle (rendered by the page in the top-left corner when the list is
   * collapsed) sits to its left without overlapping.
   */
  searchInsetForListToggle?: boolean;
}

const NOOP = (): void => undefined;

export const PublicMapShell = memo(function PublicMapShell({
  token,
  config,
  initialBasemapId,
  withAddressSearch = false,
  centerBrandOnMobile = false,
  searchInsetForListToggle = false,
}: PublicMapShellProps) {
  const { t } = useTranslation();
  const { isMobile } = useDeviceLayout();
  // Hide map chrome on mobile when the detail panel expands (parity with the authenticated map).
  const hideMobileChrome = useHideMobileChrome();
  // Local-only location watch: drives the viewer's own "you" dot from the shared
  // core. Param-less = no broadcast / presence (public map). Never prompts on
  // mount; the watch starts once the "Go to My Location" toolbar button grants
  // permission.
  useLocalLocationTracking();
  const {
    mapRef,
    isMapReady,
    stableZoom,
    mapStyle,
    initialViewState,
    effectiveMinZoom,
    effectiveMaxZoom,
    resetCenter,
    isAbovePreviewPanel,
    isAboveMinimizedPanel,
    visibleTasks,
    visibleNotes,
    anyArtifactVisible,
    roadLayerVisibility,
    hasRoadLayers,
    handleMapLoad,
    handleMove,
    handleResetView,
    handleAddressSelect,
  } = usePublicMapShellState(token, config, initialBasemapId);

  return (
    <div className={styles.shell}>
      <Map
        ref={mapRef}
        initialViewState={initialViewState}
        onMove={handleMove}
        onLoad={handleMapLoad}
        mapStyle={mapStyle}
        minZoom={effectiveMinZoom}
        maxZoom={effectiveMaxZoom}
        doubleClickZoom={MAP_CONFIG.doubleClickZoom}
        dragPan={MAP_CONFIG.dragPan}
        dragRotate={!isMobile && MAP_CONFIG.dragRotate}
        scrollZoom={!isMobile && MAP_CONFIG.scrollZoom}
        keyboard={!isMobile && MAP_CONFIG.keyboard}
        attributionControl={
          !isMobile && MAP_CONFIG.attributionControl ? {} : false
        }
        // Required: full style swap (not diff) so style.load fires reliably
        // for the 2D↔3D terrain toggle in useTerrainMode
        styleDiffing={false}
      >
        {/* PL road/border layers — only those the share explicitly exposes. */}
        {isMapReady && hasRoadLayers && (
          <RoadLayersMapLibre
            layerVisibility={roadLayerVisibility}
            onRoadContextMenu={NOOP}
            isTaskFormOpen={false}
          />
        )}

        {/* Unified SDF marker + cluster source (tasks, notes, obstructions). */}
        {isMapReady && anyArtifactVisible && (
          <UnifiedAnchorSymbolLayer
            tasks={visibleTasks}
            notes={visibleNotes}
            mapRef={mapRef}
            isMapReady={isMapReady}
          />
        )}

        {/* Task line / shape geometries. */}
        {isMapReady && visibleTasks.length > 0 && (
          <TaskGeometryOverlays tasks={visibleTasks} zoom={stableZoom} />
        )}

        {/* Owner-selected custom layers, toggled via the public layers panel. */}
        {isMapReady && config.sharedCustomLayers.length > 0 && (
          <PublicCustomLayerRenderer
            token={token}
            layers={config.sharedCustomLayers}
          />
        )}

        {/* Note click handler — pins are drawn by UnifiedAnchorSymbolLayer. */}
        {isMapReady && visibleNotes.length > 0 && (
          <NoteSymbolLayer mapRef={mapRef} isMapReady={isMapReady} />
        )}

        {/* Hover preview popup for markers + geometries. */}
        {isMapReady && <MarkerHoverPreview />}

        {/* Viewer's own live-location dot — local only, no presence/broadcast.
            Mirrors how the authenticated MapInstance mounts UserLocationLayer. */}
        {isMapReady && <UserLocationLayer localOnly />}
      </Map>

      {!hideMobileChrome && (
        <>
          {withAddressSearch && (
            <AddressSearchBar
              onSelectLocation={handleAddressSelect}
              className={`${styles.search} ${isMobile ? styles.searchMobile : ''} ${searchInsetForListToggle ? styles.searchShifted : ''}`}
              placeholder={t('search.address.placeholder')}
              collapsible={isMobile}
            />
          )}

          <MapControlsToolbar
            onResetView={handleResetView}
            mapRef={mapRef}
            isMapReady={isMapReady}
            resetCenter={resetCenter}
            resetZoom={initialViewState.zoom}
            isAboveMinimizedPanel={isAboveMinimizedPanel}
            isAbovePreviewPanel={isAbovePreviewPanel}
          />

          <PublicBrandBadge
            className={`${styles.brand} ${centerBrandOnMobile ? styles.brandCentered : ''}`}
          />
        </>
      )}

      {/* Same LayersPanel as the authenticated map, rendered read-only via
          `config`. Always mounted — SlidePanel manages its own open/close
          visibility. Gating it behind hideMobileChrome would unmount the panel
          the moment its SlidePanel registers, then remount it on unregister —
          an infinite render loop. */}
      <LayersPanel config={config} />
    </div>
  );
});
